import React from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router';
import * as Sentry from '@sentry/react';
import ReactDOM from 'react-dom/client';

import {
  isPublicESignRoute,
  isPublicReferralAccessRoute
} from './lib/isPublicSignerRoute';
import { PostHog } from './lib/PostHog';

import App from './App';

try {
  window.localStorage.removeItem('palAuthSubmissionFormValues');
} catch {}

// Public eSign token routes carry bearer tokens in the URL. Skip every
// telemetry surface that captures URL or DOM contents.
const isPublicESign = isPublicESignRoute();

if (import.meta.env.PROD && !isPublicESign) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      }),
      // Buffer-mode replay records DOM mutations all session long; these limits
      // stop the recorder on grid-heavy pages instead of degrading the page.
      Sentry.replayIntegration({
        mutationBreadcrumbLimit: 750,
        mutationLimit: 1500
      })
    ],
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE),
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'UnhandledRejection',
      'Failed to fetch dynamically imported module'
    ],
    replaysSessionSampleRate: 0,
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0
  });
}

if (!isPublicESign || isPublicReferralAccessRoute()) {
  new PostHog().load();
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
